import { computed, reactive, type ComputedRef } from "vue";

import { isApiError } from "@/src/api/http";
import { clearLastApiError, setLastApiError } from "@/src/queries/errors";

/**
 * Per-operation in-flight tracking for writes that are still driven by store actions.
 *
 * Reads live in the query cache and report their own state through `useIsFetching`, but a write
 * dispatched from a component through a Vuex action has no observer attached to it. This registry
 * fills that gap while the components are converted.
 *
 * It replaces the old `store.getters.isLoading` in two ways that matter: it lives outside the
 * transport layer, so nothing in `src/api` knows about Vue or the store, and a counter rather than
 * a boolean means two concurrent calls of the same operation cannot clear each other's flag.
 *
 * The end state for a component is `useMutation`, which supersedes this entirely:
 *
 *   const { mutateAsync: publish, isPending } = useMutation({
 *       mutationFn: (description) => store.dispatch('repoStore/publishGraph', description),
 *   })
 *
 * Until then, a store action wraps its call in `trackPending` and the component reads `usePending`.
 */
const counts = reactive(new Map<string, number>());

export async function trackPending<T>(operation: string, run: () => Promise<T>): Promise<T> {
    counts.set(operation, (counts.get(operation) ?? 0) + 1);
    clearLastApiError();
    try {
        return await run();
    } catch (error) {
        if (isApiError(error)) {
            setLastApiError(error);
        }
        throw error;
    } finally {
        const next = (counts.get(operation) ?? 1) - 1;
        if (next > 0) {
            counts.set(operation, next);
        } else {
            counts.delete(operation);
        }
    }
}

export function isPending(operation: string): boolean {
    return (counts.get(operation) ?? 0) > 0;
}

/** Reactive "is any of these operations running". */
export function usePending(operations: string | string[]): ComputedRef<boolean> {
    const list = Array.isArray(operations) ? operations : [operations];
    return computed(() => list.some((operation) => (counts.get(operation) ?? 0) > 0));
}

/**
 * Operation names observed by components. Only writes and the reads that do not go through the
 * query cache need an entry here.
 */
export const OP = Object.freeze({
    // graphs
    GET_GRAPH: "getGraph",
    UPDATE_GRAPH: "updateGraph",
    DELETE_GRAPH: "deleteGraph",
    PUBLISH_GRAPH: "publishGraph",
    RUN_JOB: "runJob",
    RUN_JOBS: "runJobs",
    CANCEL_JOB: "cancelJob",
    GET_EXERCISE_GRAPH: "getExerciseGraph",
    GET_TRACES: "getTraces",
    START_DISCOVERY: "startDiscovery",
    START_ITDM_DISCOVERY: "startItdmDiscovery",
    START_TEST_DRIVEN_DISCOVERY: "startTestDrivenDiscovery",
    IMPORT_BPMN: "importBpmn",
    LOCK_GRAPH: "lockGraph",
    UNLOCK_GRAPH: "unlockGraph",
    GET_LOCK: "getLock",
    VALIDATE_GRAPH_ATTRIBUTES: "validateGraphAttributes",
    PAC_GRAMMAR: "pacGrammar",
    VALIDATE_PQL: "validatePql",

    // celonis metadata (fetched through the query cache, tracked here so store getters and
    // components observe the same reactive source)
    GET_DATAPOOLS: "getDatapools",
    GET_DATAMODELS: "getDatamodels",
    GET_ACTIVITIES: "getActivities",
    GET_TABLES: "getTables",
    GET_CASE_TABLES: "getCaseTables",
    GET_ACTIVITY_TABLES: "getActivityTables",
    GET_TABLE_COLUMNS: "getTableColumns",
    GET_TEMPLATES: "getTemplates",

    // workspaces
    GET_WORKSPACES: "getWorkspaces",
    ADD_WORKSPACE: "addWorkspace",
    DELETE_WORKSPACE: "deleteWorkspace",
    SEARCH: "search",
    CREATE_USERS_TRAINING_FOLDER: "createUsersTrainingFolder",
    FETCH_TRAINING_RESULTS: "fetchTrainingResults",

    // celonis studio
    GET_STUDIO_SPACES: "getStudioSpaces",
    GET_SPACE_URL: "getSpaceUrl",
    POST_PACKAGE_SPACE: "postPackageSpace",
    GET_CELONIS_API: "getCelonisApi",
    UPDATE_CELONIS_API: "updateCelonisApi",
    UPDATE_MCP_API: "updateMcpApi",
    GET_EMS_URL: "getEmsUrl",

    // users
    USERS: "users",
    GET_GROUPS: "getGroups",
    EDIT_USER: "editUser",
    DELETE_USER: "deleteUser",
    INVITE_USER: "inviteUser",
    RESET_PASSWORD: "resetPassword",
    TRANSFER_OWNERSHIP: "transferOwnership",
    GET_GLOBAL_PERMISSIONS: "getGlobalPermissions",
    GET_AUDIT_LOGS: "getAuditLogs",
    AUDIT_LOGS_COUNT: "auditLogsCount",
});
